import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import LeadsTableEnhanced from "@/components/LeadsTableEnhanced";
import KanbanPipeline from "@/components/KanbanPipeline";
import LeadDetails from "@/components/LeadDetailsNew";
import ImportLeads from "@/components/ImportLeads";
import CompaniesPage from "@/components/CompaniesPage";
import CompanyDetails from "@/components/CompanyDetails";
import ContactsPage from "@/components/ContactsPage";
import ContactDetails from "@/components/ContactDetails";
import TasksPage from "@/components/TasksPage";
import CalendarPage from "@/components/CalendarPage";
import EmailTemplatesPage from "@/components/EmailTemplatesPage";
import WorkflowsPage from "@/components/WorkflowsPage";
import AnalyticsPage from "@/components/AnalyticsPage";
import EmailSettingsPage from "@/components/EmailSettingsPage";
import ChatBot from "@/components/ChatBot";
import Login from "@/components/Login";
import AdminPanel from "@/components/AdminPanel";
import ProtectedRoute from "@/components/ProtectedRoute";
import UserMenu from "@/components/UserMenu";
import NotificationBell from "@/components/NotificationBell";
import NotificationSettings from "@/components/NotificationSettings";
import TutorialButton from "@/components/TutorialButton";
import OnboardingTutorial from "@/components/OnboardingTutorial";
import GDPRSettings from "@/components/GDPRSettings";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsOfService from "@/components/TermsOfService";
import CookiePolicy from "@/components/CookiePolicy";
import CookieConsent from "@/components/CookieConsent";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { Toaster } from "sonner";
import { FileSpreadsheet, Users, Home as HomeIcon, Building2, UserCircle, RefreshCw, CheckSquare, Calendar, Mail, GitBranch, BarChart3, Target } from "lucide-react";
import { SaaSManagementPanel } from "@/components/SaaSManagementPanel";
import { TenantOverview } from "@/components/TenantOverview";
import { TenantDetailPage } from "@/components/TenantDetailPage";
import { SuperAdminUsers } from "@/components/SuperAdminUsers";
import { LandingPage } from "@/components/LandingPage";
import { MultiStepRegistration } from "@/components/MultiStepRegistration";
import { EmailVerification } from "@/components/EmailVerification";
import ContactPage from "@/components/ContactPage";
import DocumentationPage from "@/components/DocumentationPage";
import CreateAccount from "@/components/docs/getting-started/CreateAccount";
import WorkspaceSetup from "@/components/docs/getting-started/WorkspaceSetup";
import InviteTeam from "@/components/docs/getting-started/InviteTeam";
import Dashboard from "@/components/docs/getting-started/Dashboard";
import CreateEditLeads from "@/components/docs/leads/CreateEdit";
import FilteringLeads from "@/components/docs/leads/Filtering";
import PipelineStages from "@/components/docs/leads/PipelineStages";
import LeadScoring from "@/components/docs/leads/Scoring";
import Enrichment from "@/components/docs/ai/Enrichment";
import Insights from "@/components/docs/ai/Insights";
import EmailGenerator from "@/components/docs/ai/EmailGenerator";
import FitScore from "@/components/docs/ai/FitScore";
import CompaniesContacts from "@/components/docs/crm/CompaniesContacts";
import TasksCalendar from "@/components/docs/crm/TasksCalendar";
import UserManagement from "@/components/docs/admin/UserManagement";
import ComingSoon from "@/components/docs/ComingSoon";
import MobileNav from "@/components/MobileNav";
import SuperAdminAPIManagement from "@/components/SuperAdminAPIManagement";

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout, updateUser } = useAuth();
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialChecked, setTutorialChecked] = useState(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check if user needs to see tutorial on first load
  useEffect(() => {
    if (user && !tutorialChecked) {
      // Show tutorial if user hasn't seen it
      if (user.has_seen_tutorial === false) {
        setShowTutorial(true);
      }
      setTutorialChecked(true);
    }
  }, [user, tutorialChecked]);

  const handleTutorialComplete = async (dontShowAgain) => {
    setShowTutorial(false);
    
    if (dontShowAgain) {
      // Update backend to mark tutorial as seen
      try {
        await fetch(`${REACT_APP_BACKEND_URL}/api/auth/tutorial-status`, {
          method: 'PUT',
          credentials: 'include'
        });
        
        // Update local user state
        if (updateUser) {
          updateUser({ ...user, has_seen_tutorial: true });
        }
      } catch (error) {
        console.error('Error updating tutorial status:', error);
      }
    }
  };

  const handleTutorialSkip = async (dontShowAgain) => {
    setShowTutorial(false);
    
    if (dontShowAgain) {
      // Update backend to mark tutorial as seen
      try {
        await fetch(`${REACT_APP_BACKEND_URL}/api/auth/tutorial-status`, {
          method: 'PUT',
          credentials: 'include'
        });
        
        // Update local user state
        if (updateUser) {
          updateUser({ ...user, has_seen_tutorial: true });
        }
      } catch (error) {
        console.error('Error updating tutorial status:', error);
      }
    }
  };

  const handleReplayTutorial = () => {
    setShowTutorial(true);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Tutorial Modal */}
      {showTutorial && (
        <OnboardingTutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
        />
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* First Row: Logo and User Menu */}
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <MobileNav user={user} onLogout={logout} />
              <Link to="/" className="flex items-center space-x-2 sm:space-x-3" data-testid="header-logo">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                <div>
                  <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" style={{fontFamily: 'Space Grotesk, sans-serif'}}>I-Match CRM</h1>
                  <p className="text-xs text-slate-500 hidden sm:block">Lead Enrichment CRM</p>
                </div>
              </Link>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2">
              {user && (
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <span className="text-xs sm:text-sm font-medium text-slate-700 hidden sm:inline" data-testid="user-greeting">
                    Hi {user.first_name || user.email.split('@')[0]}
                  </span>
                  <TutorialButton onClick={handleReplayTutorial} />
                  <NotificationBell />
                  <UserMenu user={user} onLogout={logout} />
                </div>
              )}
            </div>
          </div>
          
          {/* Second Row: Navigation Links - Hidden on mobile */}
          <div className="py-2 hidden md:block">
            <nav className="flex items-center space-x-1 overflow-x-auto">
                {/* SuperAdmin Menu */}
                {user?.is_super_admin && (
                  <>
                    <Link
                      to="/saas-admin"
                      data-testid="nav-saas-admin"
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                        location.pathname.startsWith('/saas-admin') 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>SaaS Admin</span>
                      </div>
                    </Link>
                    <Link
                      to="/saas-admin/tenants"
                      data-testid="nav-saas-tenants"
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                        location.pathname === '/saas-admin/tenants' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <Building2 className="w-4 h-4 flex-shrink-0" />
                        <span>Tenants</span>
                      </div>
                    </Link>
                    <Link
                      to="/saas-admin/users"
                      data-testid="nav-saas-users"
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                        location.pathname === '/saas-admin/users' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <Users className="w-4 h-4 flex-shrink-0" />
                        <span>Users</span>
                      </div>
                    </Link>
                    <div className="border-l border-slate-300 h-6 mx-2"></div>
                  </>
                )}
                
                {/* Regular/Tenant Menu - Hide for SuperAdmin */}
                {!user?.is_super_admin && (
                  <>
                <Link
                  to="/"
                  data-testid="nav-leads"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname === '/' 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <HomeIcon className="w-4 h-4 flex-shrink-0" />
                    <span>Leads</span>
                  </div>
                </Link>
                <Link
                  to="/companies"
                  data-testid="nav-companies"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/companies') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <span>Companies</span>
                  </div>
                </Link>
                <Link
                  to="/contacts"
                  data-testid="nav-contacts"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/contacts') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <UserCircle className="w-4 h-4 flex-shrink-0" />
                    <span>Contacts</span>
                  </div>
                </Link>
                <Link
                  to="/tasks"
                  data-testid="nav-tasks"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/tasks') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <CheckSquare className="w-4 h-4 flex-shrink-0" />
                    <span>Tasks</span>
                  </div>
                </Link>
                <Link
                  to="/calendar"
                  data-testid="nav-calendar"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/calendar') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>Calendar</span>
                  </div>
                </Link>
                <Link
                  to="/email-templates"
                  data-testid="nav-email-templates"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/email-templates') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>Templates</span>
                  </div>
                </Link>
                <Link
                  to="/workflows"
                  data-testid="nav-workflows"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/workflows') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <GitBranch className="w-4 h-4 flex-shrink-0" />
                    <span>Workflows</span>
                  </div>
                </Link>
                <Link
                  to="/analytics"
                  data-testid="nav-analytics"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/analytics') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <BarChart3 className="w-4 h-4 flex-shrink-0" />
                    <span>Analytics</span>
                  </div>
                </Link>
                <Link
                  to="/email-settings"
                  data-testid="nav-email-settings"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname.startsWith('/email-settings') 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>Email Sync</span>
                  </div>
                </Link>
                <Link
                  to="/import"
                  data-testid="nav-import"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    location.pathname === '/import' 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <FileSpreadsheet className="w-4 h-4 flex-shrink-0" />
                    <span>Import</span>
                  </div>
                </Link>
                  </>
                )}
              </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {children}
      </main>
    </div>
  );
};

const MainContent = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("table"); // "table" or "kanban"
  
  const handleViewChange = (newView) => {
    setView(newView);
    navigate("/");
  };
  
  const handleLeadCreated = (leadId) => {
    // Refresh leads table
    setView("table");
    navigate("/");
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          view === "table" 
            ? <LeadsTableEnhanced onViewKanban={() => setView("kanban")} /> 
            : <KanbanPipeline onViewTable={() => setView("table")} />
        } />
        <Route path="/leads/:id" element={<LeadDetails />} />
        <Route path="/companies" element={<CompaniesPage />} />
        <Route path="/companies/:id" element={<CompanyDetails />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactDetails />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/email-templates" element={<EmailTemplatesPage />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/email-settings" element={<EmailSettingsPage />} />
        <Route path="/import" element={<ImportLeads />} />
      </Routes>
      <ChatBot onLeadCreated={handleLeadCreated} />
    </>
  );
};

const AppContent = () => {
  const { user, loading, login } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Landing Page - Public */}
        <Route path="/home" element={<LandingPage />} />
        
        {/* Contact Page - Public */}
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Documentation Page - Public */}
        <Route path="/documentation" element={<DocumentationPage />} />
        
        {/* Documentation Articles */}
        <Route path="/docs/getting-started/create-account" element={<CreateAccount />} />
        <Route path="/docs/getting-started/workspace-setup" element={<WorkspaceSetup />} />
        <Route path="/docs/getting-started/invite-team" element={<InviteTeam />} />
        <Route path="/docs/getting-started/dashboard" element={<Dashboard />} />
        <Route path="/docs/leads/create-edit" element={<CreateEditLeads />} />
        <Route path="/docs/leads/filtering" element={<FilteringLeads />} />
        <Route path="/docs/leads/pipeline-stages" element={<PipelineStages />} />
        <Route path="/docs/leads/scoring" element={<LeadScoring />} />
        <Route path="/docs/ai/enrichment" element={<Enrichment />} />
        <Route path="/docs/ai/insights" element={<Insights />} />
        <Route path="/docs/ai/email-generator" element={<EmailGenerator />} />
        <Route path="/docs/ai/fit-score" element={<FitScore />} />
        <Route path="/docs/crm/companies-contacts" element={<CompaniesContacts />} />
        <Route path="/docs/crm/tasks-calendar" element={<TasksCalendar />} />
        <Route path="/docs/admin/user-management" element={<UserManagement />} />
        <Route path="/docs/:category/:article" element={<ComingSoon />} />
        
        {/* Registration - Public */}
        <Route path="/register" element={
          user ? <Navigate to="/" replace /> : <MultiStepRegistration />
        } />
        
        {/* Email Verification - Public */}
        <Route path="/verify-email" element={<EmailVerification />} />
        
        <Route path="/login" element={
          user ? <Navigate to="/" replace /> : <Login onLoginSuccess={login} />
        } />
        
        {/* Public Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        
        {/* GDPR Settings - Protected */}
        <Route path="/gdpr-settings" element={
          <ProtectedRoute>
            <Layout>
              <GDPRSettings />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Notification Settings - Protected */}
        <Route path="/notifications-settings" element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminPanel />
          </ProtectedRoute>
        } />
        
        {/* SuperAdmin SaaS Management Routes */}
        <Route path="/saas-admin" element={
          <ProtectedRoute>
            <SaaSManagementPanel />
          </ProtectedRoute>
        } />
        
        <Route path="/saas-admin/tenants" element={
          <ProtectedRoute>
            <TenantOverview />
          </ProtectedRoute>
        } />
        
        <Route path="/saas-admin/tenants/:tenantId" element={
          <ProtectedRoute>
            <TenantDetailPage />
          </ProtectedRoute>
        } />
        
        <Route path="/saas-admin/users" element={
          <ProtectedRoute>
            <SuperAdminUsers />
          </ProtectedRoute>
        } />
        
        <Route path="/saas-admin/tenants/:tenantId/api-management" element={
          <ProtectedRoute>
            <SuperAdminAPIManagement />
          </ProtectedRoute>
        } />
        
        <Route path="/*" element={
          <ProtectedRoute>
            <Layout>
              <MainContent />
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <AppContent />
            <Toaster position="top-right" closeButton richColors />
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
